

import { useState, useEffect, useMemo } from "react";
import DashboardLayout from "../components/DashboardLayout";
import PackageModal from "./PackageModal";
import { mastersApi, vendorsApi } from "@/lib/api";
import { useToast } from "@/context/ToastContext";
import { useAuth } from "@/hooks/useAuth";
import { formatCurrency } from "@/lib/data";
import { Gift, Edit2, Trash2, Plus, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, Download, CheckSquare, Square, Power } from "lucide-react";
import { PackageFilters } from "./components";

interface Service {
  id: number;
  name: string;
  selling_price: number;
  base_price: number;
  vendor_id?: number;
  vendor?: { id: number; name: string };
}

interface Vendor {
  id: number;
  name: string;
  commission_type?: string;
  commission_value?: number;
}

interface Package {
  id: number;
  name: string;
  description?: string;
  base_price: string;
  offered_price?: string;
  is_active: boolean;
  inclusions?: string[];
  services_data?: Service[];
  service_ids?: number[];
}

export default function PackagesPage() {
  const { showToast } = useToast();
  const { user } = useAuth();
  const isStaff = user?.role === 'staff';
  const [packages, setPackages] = useState<Package[]>([]);
  const [services, setServices] = useState<Service[]>([]);
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState<Package | null>(null);
  
  // Filter and sort state
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [sortField, setSortField] = useState<'name' | 'base_price' | 'service_count' | 'is_active'>('name');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);
  
  // Bulk actions state
  const [selectedPackages, setSelectedPackages] = useState<Set<number>>(new Set());
  const [showBulkActions, setShowBulkActions] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [packagesRes, servicesRes, vendorsRes] = await Promise.all([
        mastersApi.packages.list(),
        mastersApi.services.list(),
        vendorsApi.list(),
      ]);
      
      const packagesData = packagesRes.data?.data || packagesRes.data || [];
      setPackages(packagesData);
      setServices(servicesRes.data?.data || servicesRes.data || []);
      setVendors(vendorsRes.data?.data || vendorsRes.data || []);
    } catch (error) {
      showToast("Failed to load data", "error");
    } finally {
      setLoading(false);
    }
  };

  // Handle sort
  const handleSort = (field: 'name' | 'base_price' | 'service_count' | 'is_active') => {
    if (sortField === field) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: 'name' | 'base_price' | 'service_count' | 'is_active') => {
    if (sortField !== field) return <ArrowUpDown className="w-4 h-4 text-stone-400" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-4 h-4 text-amber-600" /> : 
      <ArrowDown className="w-4 h-4 text-amber-600" />;
  };

  const handleDelete = async (id: number) => {
    if (!confirm("Delete this package?")) return;
    try {
      await mastersApi.packages.delete(id);
      showToast("Package deleted", "success");
      loadData();
    } catch (error) {
      showToast("Failed to delete", "error");
    }
  };

  const handleFormSubmit = async (data: any) => {
    try {
      if (editingPackage) {
        await mastersApi.packages.update(editingPackage.id, data);
        showToast("Package updated", "success");
      } else {
        await mastersApi.packages.create(data);
        showToast("Package created", "success");
      }
      setIsModalOpen(false);
      setEditingPackage(null);
      loadData();
    } catch (error) {
      showToast("Failed to save", "error");
    }
  };

  // Filter and sort packages
  const filteredAndSortedPackages = useMemo(() => {
    let filtered = packages;
    
    // Apply search filter
    if (search) {
      const searchLower = search.toLowerCase();
      filtered = filtered.filter(pkg => 
        pkg.name.toLowerCase().includes(searchLower) ||
        (pkg.description && pkg.description.toLowerCase().includes(searchLower))
      );
    }
    
    // Apply status filter
    if (statusFilter === 'active') {
      filtered = filtered.filter(pkg => pkg.is_active);
    } else if (statusFilter === 'inactive') {
      filtered = filtered.filter(pkg => !pkg.is_active);
    }
    
    // Apply sorting
    filtered = [...filtered].sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'base_price':
          comparison = Number(a.base_price) - Number(b.base_price);
          break;
        case 'service_count':
          comparison = (a.services_data?.length || 0) - (b.services_data?.length || 0);
          break;
        case 'is_active':
          comparison = (a.is_active === b.is_active) ? 0 : (a.is_active ? -1 : 1);
          break;
      }
      
      return sortDirection === 'asc' ? comparison : -comparison;
    });
    
    return filtered;
  }, [packages, search, statusFilter, sortField, sortDirection]);

  // Pagination
  const paginatedPackages = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedPackages.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedPackages, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedPackages.length / itemsPerPage);
  
  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
    setSelectedPackages(new Set()); // Clear selection on filter change
  }, [search, statusFilter, sortField, sortDirection]);
  
  // Show bulk actions when items are selected
  useEffect(() => {
    setShowBulkActions(selectedPackages.size > 0);
  }, [selectedPackages]);
  
  // Handle bulk selection
  const togglePackageSelection = (id: number) => {
    setSelectedPackages(prev => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };
  
  const toggleAllPackages = () => {
    if (selectedPackages.size === paginatedPackages.length) {
      setSelectedPackages(new Set());
    } else {
      setSelectedPackages(new Set(paginatedPackages.map(p => p.id)));
    }
  };
  
  // Handle bulk activate/deactivate
  const handleBulkToggleStatus = async (newStatus: boolean) => {
    if (!confirm(`Set ${selectedPackages.size} packages to ${newStatus ? 'active' : 'inactive'}?`)) return;
    
    try {
      // Update each package
      for (const id of selectedPackages) {
        const pkg = packages.find(p => p.id === id);
        if (pkg) {
          await mastersApi.packages.update(id, { ...pkg, is_active: newStatus });
        }
      }
      showToast(`${selectedPackages.size} packages updated`, "success");
      setSelectedPackages(new Set());
      loadData();
    } catch (error) {
      showToast("Failed to update packages", "error");
    }
  };
  
  // Handle bulk delete
  const handleBulkDelete = async () => {
    if (!confirm(`Delete ${selectedPackages.size} packages? This action cannot be undone.`)) return;
    
    try {
      for (const id of selectedPackages) {
        await mastersApi.packages.delete(id);
      }
      showToast(`${selectedPackages.size} packages deleted`, "success");
      setSelectedPackages(new Set());
      loadData();
    } catch (error) {
      showToast("Failed to delete packages", "error");
    }
  };
  
  // Handle CSV export
  const handleExportCSV = () => {
    const packagesToExport = selectedPackages.size > 0 
      ? packages.filter(p => selectedPackages.has(p.id))
      : filteredAndSortedPackages;
    
    const headers = ['Package Name', 'Description', 'Base Price', 'Offered Price', 'Status', 'Services Count', 'Inclusions'];
    const rows = packagesToExport.map(pkg => [
      pkg.name,
      pkg.description || '',
      Number(pkg.base_price) || 0,
      Number(pkg.offered_price) || 0,
      pkg.is_active ? 'Active' : 'Inactive',
      pkg.services_data?.length || 0,
      pkg.inclusions?.join(', ') || '',
    ]);
    
    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `packages-export-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    if (selectedPackages.size > 0) {
      setSelectedPackages(new Set());
    }
    showToast('CSV exported successfully', 'success');
  };


  if (loading) {
    return (
      <DashboardLayout>
        {/* Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
          <div>
            <div className="h-6 w-32 bg-stone-200 rounded animate-pulse mb-2" />
            <div className="h-4 w-48 bg-stone-200 rounded animate-pulse" />
          </div>
          <div className="h-9 w-32 bg-stone-200 rounded-xl animate-pulse" />
        </div>


        {/* Filters Skeleton */}
        <div className="h-16 bg-stone-100 rounded-xl border border-stone-200 mb-4 animate-pulse" />

        {/* Sort Controls Skeleton */}
        <div className="h-10 bg-stone-100 rounded-lg mb-4 animate-pulse" />

        {/* Table Skeleton */}
        <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
          <div className="h-12 bg-stone-100 border-b border-stone-200 animate-pulse" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-16 border-b border-stone-100 animate-pulse" />
          ))}
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      {/* Header */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="text-xl font-bold text-stone-800">Packages</h1>
          <p className="text-sm text-stone-500">Event packages with bundled services</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2 rounded-xl bg-stone-100 text-stone-700 font-semibold text-sm flex items-center gap-2 hover:bg-stone-200 transition-colors"
          >
            <Download className="w-4 h-4" />
            Export
          </button>
          {!isStaff && (
            <button 
              onClick={() => { setEditingPackage(null); setIsModalOpen(true); }}
              className="px-4 py-2 rounded-xl gold-gradient text-white font-semibold text-sm flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              New Package
            </button>
          )}
        </div>
      </div>


      {/* Filters */}
      <PackageFilters
        search={search}
        setSearch={setSearch}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        onApply={() => {}} // Filters are applied reactively
      />

      {/* Bulk Actions Bar */}
      {showBulkActions && !isStaff && (
        <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-xl flex items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-amber-800">{selectedPackages.size} packages selected</span>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => handleBulkToggleStatus(true)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-100 text-emerald-700 text-sm font-medium hover:bg-emerald-200 transition-colors"
            >
              <Power className="w-4 h-4" />
              Activate
            </button>
            <button
              onClick={() => handleBulkToggleStatus(false)}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-stone-100 text-stone-700 text-sm font-medium hover:bg-stone-200 transition-colors"
            >
              <Power className="w-4 h-4" />
              Deactivate
            </button>
            <button
              onClick={handleBulkDelete}
              className="flex items-center gap-2 px-3 py-2 rounded-lg bg-rose-100 text-rose-700 text-sm font-medium hover:bg-rose-200 transition-colors"
            >
              <Trash2 className="w-4 h-4" />
              Delete
            </button>
            <button
              onClick={() => setSelectedPackages(new Set())}
              className="px-3 py-2 rounded-lg bg-white text-stone-600 text-sm font-medium hover:bg-stone-100 transition-colors border border-stone-300"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Sort and View Controls */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm text-stone-500">Sort by:</span>
          <button
            onClick={() => handleSort('name')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'name' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Name {getSortIcon('name')}
          </button>
          <button
            onClick={() => handleSort('base_price')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'base_price' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Price {getSortIcon('base_price')}
          </button>
          <button
            onClick={() => handleSort('service_count')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'service_count' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Services {getSortIcon('service_count')}
          </button>
          <button
            onClick={() => handleSort('is_active')}
            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
              sortField === 'is_active' 
                ? 'bg-amber-100 text-amber-700' 
                : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
            }`}
          >
            Status {getSortIcon('is_active')}
          </button>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Per Page Selector */}
          <select
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="text-xs border border-stone-300 rounded px-2 py-1.5 bg-white"
          >
            <option value={10}>10 per page</option>
            <option value={25}>25 per page</option>
            <option value={50}>50 per page</option>
            <option value={100}>100 per page</option>
          </select>
        </div>
      </div>

      {/* Packages List */}
      <div className="bg-white rounded-xl border border-stone-200 overflow-hidden">
        <table className="w-full">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {!isStaff && (
                <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider w-10">
                  <button
                    onClick={toggleAllPackages}
                    className="hover:text-amber-600 transition-colors"
                  >
                    {selectedPackages.size === paginatedPackages.length && paginatedPackages.length > 0 ? (
                      <CheckSquare className="w-4 h-4" />
                    ) : (
                      <Square className="w-4 h-4" />
                    )}
                  </button>
                </th>
              )}
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Package</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Price</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Services</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Status</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-stone-600 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-stone-200">
            {paginatedPackages.map((pkg) => {
              const isSelected = selectedPackages.has(pkg.id);
              return (
                <tr key={pkg.id} className={`hover:bg-stone-50 transition-colors ${isSelected ? 'bg-amber-50' : ''}`}>
                  {!isStaff && (
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => togglePackageSelection(pkg.id)}
                        className="hover:text-amber-600 transition-colors"
                      >
                        {isSelected ? (
                          <CheckSquare className="w-4 h-4 text-amber-600" />
                        ) : (
                          <Square className="w-4 h-4 text-stone-400" />
                        )}
                      </button>
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <div>
                      <p className="font-medium text-stone-800">{pkg.name}</p>
                      {pkg.description && (
                        <p className="text-xs text-stone-500 line-clamp-1">{pkg.description}</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <p className="font-semibold text-stone-700">{formatCurrency(Number(pkg.base_price) || 0)}</p>
                    {pkg.offered_price && (
                      <p className="text-xs text-stone-500">Offer: {formatCurrency(Number(pkg.offered_price) || 0)}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-stone-600">{pkg.services_data?.length || 0} services</p>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                      pkg.is_active 
                        ? 'bg-emerald-100 text-emerald-700' 
                        : 'bg-stone-100 text-stone-600'
                    }`}>
                      {pkg.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    {!isStaff && (
                      <div className="flex items-center justify-center gap-2">
                        <button 
                          onClick={() => { setEditingPackage(pkg); setIsModalOpen(true); }}
                          className="p-2 rounded-lg bg-stone-100 text-stone-600 hover:bg-stone-200 transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                        <button 
                          onClick={() => handleDelete(pkg.id)}
                          className="p-2 rounded-lg bg-rose-50 text-rose-600 hover:bg-rose-100 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Empty State */}
      {paginatedPackages.length === 0 && (
        <div className="text-center py-12 bg-stone-50 rounded-xl border border-stone-200">
          <Gift className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <p className="text-stone-500">
            {search || statusFilter ? 'No packages match your filters' : 'No packages yet'}
          </p>
          {!isStaff && !search && !statusFilter && (
            <button 
              onClick={() => setIsModalOpen(true)}
              className="mt-3 px-4 py-2 rounded-xl gold-gradient text-white text-sm font-medium"
            >
              Create First Package
            </button>
          )}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-6">
          <p className="text-sm text-stone-500">
            Showing {((currentPage - 1) * itemsPerPage) + 1} to {Math.min(currentPage * itemsPerPage, filteredAndSortedPackages.length)} of {filteredAndSortedPackages.length} packages
          </p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="p-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <div className="flex items-center gap-1">
              {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`w-8 h-8 rounded-lg text-sm font-medium transition-colors ${
                      currentPage === pageNum 
                        ? 'bg-amber-500 text-white' 
                        : 'border border-stone-300 text-stone-600 hover:bg-stone-50'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <button
              onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="p-2 rounded-lg border border-stone-300 text-stone-600 hover:bg-stone-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Full Screen Package Modal */}
      <PackageModal
        isOpen={isModalOpen}
        initialData={editingPackage as any}
        onSubmit={handleFormSubmit}
        onCancel={() => { setIsModalOpen(false); setEditingPackage(null); }}
        services={services as any}
        vendors={vendors}
      />
    </DashboardLayout>
  );
}
